import type { PermissionsConfig } from '../../../hooks/usePermissionsConfig';

export interface StrippedPermission {
  module: string;
  action: string;
  reason: 'unknown-module' | 'unsupported-action';
}

export interface SanitizeResult {
  /** Permissions safe to send to the API. */
  clean: Record<string, string[]>;
  /** Pairs that were dropped, for telemetry / user-facing notices. */
  stripped: StrippedPermission[];
}

/**
 * Strict sanitiser — drops any `(module, action)` pair that the live
 * `permissions-config` does NOT declare. Mirrors the backend's role
 * permissions validator exactly so the client can never produce a payload
 * the server will reject with `422 Invalid permissions structure`.
 *
 * Why this exists: a role may carry historical grants where the action is
 * no longer declared on the module (e.g. an old `dashboard.import` grant
 * after the dashboard module's action set was narrowed to `[read]`, or a
 * grant on a module that was renamed). The matrix surfaces these as
 * "stale" but they remain in the form value until the user unticks them.
 * This util guarantees nothing stale ever leaves the client.
 *
 * Per project policy: canonical-only data, no alias-mapping or
 * self-healing. Stale grants are dropped, not migrated.
 */
export function sanitizePermissions(
  permissions: Record<string, string[]> | undefined,
  config: PermissionsConfig | undefined,
): SanitizeResult {
  if (!permissions || !config) {
    return { clean: {}, stripped: [] };
  }
  const moduleSupport = new Map(
    config.modules.map(m => [m.key, new Set(m.actions)]),
  );
  const clean: Record<string, string[]> = {};
  const stripped: StrippedPermission[] = [];

  for (const [moduleKey, actions] of Object.entries(permissions)) {
    if (!Array.isArray(actions) || actions.length === 0) continue;
    const supported = moduleSupport.get(moduleKey);
    if (!supported) {
      for (const a of actions) {
        stripped.push({ module: moduleKey, action: a, reason: 'unknown-module' });
      }
      continue;
    }
    const kept: string[] = [];
    for (const a of actions) {
      if (supported.has(a)) kept.push(a);
      else stripped.push({ module: moduleKey, action: a, reason: 'unsupported-action' });
    }
    if (kept.length > 0) clean[moduleKey] = kept;
  }
  return { clean, stripped };
}

/**
 * Small helper for toasts: summarise a stripped list as a short sample
 * suitable for inline UI ("dashboard.import, dashboard.create +2 more").
 */
export function summarizeStripped(stripped: StrippedPermission[], sampleSize = 3): string {
  if (stripped.length === 0) return '';
  const sample = stripped
    .slice(0, sampleSize)
    .map(s => `${s.module}.${s.action}`)
    .join(', ');
  const more = stripped.length > sampleSize ? ` (+${stripped.length - sampleSize} more)` : '';
  return `${sample}${more}`;
}
